import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { useCmsContent } from './CmsContentContext';
import './editable-image.css';

const ImageEditorContext = createContext(null);
const validImageUrl = (value) => {
  if (/^\/cms-api\/images\/[a-f0-9]{64}\.(png|jpg|gif|webp)$/.test(value)) return true;
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password; } catch { return false; }
};

function ImageDialog({ selection, close }) {
  const { update, valueFor } = useCmsContent();
  const [url, setUrl] = useState(valueFor(selection.contentKey) || '');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  const value = url.trim();
  useEffect(() => () => controller.current?.abort(), []);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > 8 * 1024 * 1024) {
      setError('Choose a JPG, PNG, WebP, or GIF image under 8 MB.');
      return;
    }
    setBusy(true);
    controller.current = new AbortController();
    try {
      const response = await fetch('/cms-api/images', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': file.type }, body: file, signal: controller.current.signal });
      const body = await response.json();
      if (!response.ok) throw new Error(response.status === 401 ? 'Your session expired. Sign in again before uploading.' : body.error || 'Upload failed. Try again.');
      setReady(false);
      setUrl(body.url);
    } catch (failure) {
      if (failure.name !== 'AbortError') setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="cms-image-overlay" />
        <Dialog.Content className="cms-image-dialog" onCloseAutoFocus={(event) => { event.preventDefault(); selection.trigger?.focus(); }}>
          <Dialog.Title>Change image</Dialog.Title>
          <Dialog.Description>Upload an image or paste an HTTPS image link. Use Save in the editor to publish your changes.</Dialog.Description>
          <label className="cms-image-upload">Upload image<input aria-label="Upload image" type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={busy} onChange={upload} /></label>
          <p className="cms-image-hint">JPG, PNG, WebP, or GIF. Maximum 8 MB.</p>
          <label className="cms-image-url">Image URL<input type="url" value={url} disabled={busy} onChange={(event) => { setUrl(event.target.value); setReady(false); setError(''); }} /></label>
          {validImageUrl(value) && <img key={value} className="cms-image-preview" src={value} alt="Replacement preview" onLoad={() => setReady(true)} onError={() => { setReady(false); setError('This image could not be loaded. Choose another file or link.'); }} />}
          {value && !validImageUrl(value) && <p role="alert">Enter a valid HTTPS image link.</p>}
          {error && <p role="alert" className="cms-image-error">{error}</p>}
          <p role="status">{busy ? 'Uploading image…' : ''}</p>
          <div className="cms-image-actions">
            <Dialog.Close asChild><button type="button">Cancel</button></Dialog.Close>
            <button type="button" disabled={busy || !ready || !validImageUrl(value)} onClick={() => { update(selection.contentKey, value); close(); }}>Use image</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ImageEditingProvider({ children }) {
  const [selection, setSelection] = useState(null);
  return <ImageEditorContext.Provider value={setSelection}>{children}{selection && <ImageDialog key={selection.contentKey} selection={selection} close={() => setSelection(null)} />}</ImageEditorContext.Provider>;
}

export function EditableImage({ contentKey, alt, className = '', ...props }) {
  const { editing, loaded, saving, valueFor } = useCmsContent();
  const open = useContext(ImageEditorContext);
  const ref = useRef(null);
  const edit = () => open({ contentKey, trigger: ref.current });
  const imageProps = { ...props, src: valueFor(contentKey), alt, className };
  if (!editing || !loaded || saving || !open) return <img {...imageProps} />;
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <img {...imageProps} ref={ref} className={`${className} cms-editable-image`} data-cms-image={contentKey} tabIndex={0} role="button" aria-label={`Change image: ${alt || 'site image'}`} title="Right-click to change image, or click to choose a replacement" onClick={(event) => { event.preventDefault(); event.stopPropagation(); edit(); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); edit(); } }} />
      </ContextMenu.Trigger>
      <ContextMenu.Portal><ContextMenu.Content className="cms-image-menu"><ContextMenu.Item onSelect={edit}>Change image…</ContextMenu.Item></ContextMenu.Content></ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
